import React, { createContext, useReducer, useMemo } from "react";
import { Metric, RiskIndicatorMetric, MetricReport, Query, stringifyQuery, riskIndicatormetricFor } from "../lib/api/charts";
import { Loading } from "./base";

type MetricSet = { metric: Metric; loading: Loading };
type RiskIndicatorMetricSet = { metric: RiskIndicatorMetric; loading: Loading };

export type RiskIndicatorMetricContextState = {
  metric: {
    [index: string]: RiskIndicatorMetricSet;
  };
  getMetric(report: MetricReport, query: Query): Partial<RiskIndicatorMetricSet>;
};

export const RiskIndicatorMetricContext = createContext<RiskIndicatorMetricContextState>({
  metric: {},
  getMetric: () => ({}),
});

type QueryAction = {
  type: "PATCH_METRIC";
  id: string;
  data: Partial<RiskIndicatorMetricSet>;
};

const defaultValue = {
  value: 0,
  formattedValue: "",
};

const defaultMetric: RiskIndicatorMetricSet = {
  metric: {
    title: "",
    actualTitle: "",
    classification: "income",
    actual: {
      ...defaultValue,
      risk: { noRisk: { value: 0, formatted: "N/A" }, risk: { value: 0, formatted: "N/A" }, earlyWarning: { value: 0, formatted: "N/A" }, warning: { value: 0, formatted: "N/A" } },
      previous: { noRisk: { value: 0, formatted: "N/A" }, risk: { value: 0, formatted: "N/A" }, earlyWarning: { value: 0, formatted: "N/A" }, warning: { value: 0, formatted: "N/A" } },
      previousVar: { noRisk: { value: 0, formatted: "N/A" }, risk: { value: 0, formatted: "N/A" }, earlyWarning: { value: 0, formatted: "N/A" }, warning: { value: 0, formatted: "N/A" } }
    },
  },
  loading: { loaded: false, loading: true, error: null },
};

export const RiskIndicatorMetricProvider = ({ children }) => {
  const memo = (report: MetricReport, query: Query) => `${report}-${stringifyQuery(query)}`;

  const [metric, dispatchMetric] = useReducer((state: { [index: string]: RiskIndicatorMetricSet }, action: QueryAction) => {
    switch (action.type) {
      case "PATCH_METRIC":
        return {
          ...state,
          [action.id]: {
            ...state[action.id],
            ...action.data,
          },
        };
      default:
        return state;
    }
  }, {});

  const value = useMemo(
    () => ({
      metric,
      getMetric: (report: MetricReport, query: Query) => {
        const id = memo(report, query);
        const set = metric[id];

        if (set) {
          return set;
        }

        dispatchMetric({
          type: "PATCH_METRIC",
          id,
          data: defaultMetric,
        });

        riskIndicatormetricFor(query, report)
          .then(metric => {
            dispatchMetric({
              type: "PATCH_METRIC",
              id,
              data: {
                metric: metric,
                loading: {
                  loaded: true,
                  loading: false,
                  error: null,
                },
              },
            });
          })
          .catch(e => {
            dispatchMetric({
              type: "PATCH_METRIC",
              id,
              data: {
                loading: {
                  loaded: false,
                  loading: false,
                  error: e,
                },
              },
            });
          });

        return defaultMetric;
      },
    }),
    [metric],
  );
  return <RiskIndicatorMetricContext.Provider value={value}>{children}</RiskIndicatorMetricContext.Provider>;
};
